/**
 * FENCES (2026-10-04, Matt: "I need a new type family called fences and I want them to be
 * similar to railings in that we can draw them with a line. They could be able to host to
 * and follow terrains … a few typical types of fences prepopulated … They should be able to
 * host doors (gates). Their height should be adjustable, similar to how walls heights are
 * adjustable.") Decisions (asked one at a time, 2026-10-04):
 *   - slopes: each TYPE carries a default (stepped or raked); any fence can switch;
 *   - terrain: like railings — drawn flat, hosted afterwards via "Host to…" onto terrain,
 *     floors/decks (and stairs/ramps), any number at once (`hostIds`);
 *   - height: height above the ground (the type's, overridable per fence) + a base offset;
 *   - types: wood privacy, horizontal slat, ornamental metal picket, aluminum pool, glass
 *     pool, post-and-rail ranch, chain link, stucco/masonry site wall;
 *   - gates: the gate door types (swing / slide) host into a fence (later stage);
 *   - plan: true-thickness line + a square per post;
 *   - posts: laid out automatically, then draggable/addable/removable (later stage).
 *
 * The construction FAMILY (`fenceStyle`) picks the builder; every size is a firm standard on
 * the type. Geometry: bim-geometry categories/fence/generate3d.ts (the railing's per-station
 * host height field, shared).
 */
import type { CategorySchema } from '../../registry'

export const FENCE_STYLES = ['boards', 'slats', 'picket', 'glass', 'rail', 'chainLink', 'masonry', 'doubleFace', 'barPicket'] as const
export type FenceStyle = (typeof FENCE_STYLES)[number]

const IN = 1 / 12
const on = (styles: FenceStyle[]) => ({ param: 'fenceStyle', equals: styles as unknown as string[] })

export const fenceSchema: CategorySchema = {
  id: 'fence',
  pathParamKey: 'path',
  label: 'Fence',
  hostable: { hostsInto: ['stair', 'ramp', 'floor', 'topo'] },
  typeParams: {
    groups: [
      {
        label: 'Construction',
        params: [
          {
            key: 'fenceStyle', label: 'Style', kind: 'choice', default: 'boards',
            choices: [
              { value: 'boards', label: 'Vertical Boards (privacy)' },
              { value: 'slats', label: 'Horizontal Slats' },
              { value: 'picket', label: 'Metal Pickets' },
              { value: 'glass', label: 'Glass Panels on Spigots' },
              { value: 'rail', label: 'Post & Rail' },
              { value: 'chainLink', label: 'Chain Link' },
              { value: 'masonry', label: 'Masonry / Stucco Wall' },
              // DOUBLE-FACE (2026-10-05, Matt's detail): boards both faces on 2x6 rails
              // sandwiching steel pipe posts, 4" bands top + bottom, a flat 2x8 cap
              { value: 'doubleFace', label: 'Double-Face Boards on Pipe Posts, Capped' },
              // BAR PICKET (2026-10-05, Matt): square wood pickets on a rhythm, round steel bars
              // threaded through their centers top + bottom; every Nth picket is a steel post
              // clad in wood — identical to the rest, so the support disappears
              { value: 'barPicket', label: 'Wood Pickets on Through-Bars (hidden clad-steel posts)' },
            ],
          },
          { key: 'heightFt', label: 'Height', kind: 'length', default: 6, min: 1, max: 12 },
          {
            key: 'slopeMode', label: 'On Slopes', kind: 'choice', default: 'stepped',
            choices: [
              { value: 'stepped', label: 'Stepped (panels level, step at posts)' },
              { value: 'raked', label: 'Raked (follows the grade)' },
            ],
          },
          // (bar pickets: posts are every Nth picket instead — `postEvery` below)
          { key: 'postSpacingFt', label: 'Post Spacing', kind: 'length', default: 8, min: 2, max: 12,
            conditionalOn: { param: 'fenceStyle', equals: ['boards', 'slats', 'picket', 'glass', 'rail', 'chainLink', 'masonry', 'doubleFace'] } },
          { key: 'postSizeFt', label: 'Post Size', kind: 'length', default: 3.5 * IN, min: 1 * IN, max: 2 },
          { key: 'clearanceFt', label: 'Gap Above Ground', kind: 'length', default: 2 * IN, min: 0, max: 1 },
        ],
      },
      {
        label: 'Infill',
        params: [
          { key: 'boardWidthFt', label: 'Board Width', kind: 'length', default: 5.5 * IN, min: 1 * IN, max: 1, conditionalOn: on(['boards', 'slats', 'doubleFace']) },
          { key: 'boardGapFt', label: 'Gap Between Boards', kind: 'length', default: 0, min: 0, max: 0.5, conditionalOn: on(['boards', 'slats']) },
          { key: 'boardThickFt', label: 'Board Thickness', kind: 'length', default: 0.75 * IN, min: 0.25 * IN, max: 3 * IN, conditionalOn: on(['boards', 'slats', 'doubleFace']) },
          { key: 'bandHeightFt', label: 'Band Height (top & bottom)', kind: 'length', default: 4 * IN, min: 0, max: 1, conditionalOn: on(['doubleFace']) },
          { key: 'capWidthFt', label: 'Cap Width', kind: 'length', default: 7.25 * IN, min: 1 * IN, max: 2, conditionalOn: on(['doubleFace']) },
          { key: 'capThickFt', label: 'Cap Thickness', kind: 'length', default: 1.5 * IN, min: 0.5 * IN, max: 0.5, conditionalOn: on(['doubleFace']) },
          { key: 'picketSizeFt', label: 'Picket Size', kind: 'length', default: 0.75 * IN, min: 0.25 * IN, max: 3 * IN, conditionalOn: on(['picket', 'barPicket']) },
          { key: 'picketSpacingFt', label: 'Picket Spacing (on center)', kind: 'length', default: 4.75 * IN, min: 2 * IN, max: 1, conditionalOn: on(['picket', 'barPicket']) },
          { key: 'postEvery', label: 'Post Every (pickets)', kind: 'number', default: 4, min: 2, max: 12, conditionalOn: on(['barPicket']) },
          { key: 'coreSizeFt', label: 'Steel Post Core', kind: 'length', default: 1 * IN, min: 0.5 * IN, max: 6 * IN, conditionalOn: on(['barPicket']) },
          { key: 'barDiameterFt', label: 'Bar Diameter', kind: 'length', default: 0.75 * IN, min: 0.25 * IN, max: 2 * IN, conditionalOn: on(['barPicket']) },
          { key: 'barInsetFt', label: 'Bars In From Top & Bottom', kind: 'length', default: 6 * IN, min: 1 * IN, max: 2, conditionalOn: on(['barPicket']) },
          { key: 'railCount', label: 'Rails', kind: 'number', default: 3, min: 1, max: 6, conditionalOn: on(['rail']) },
          { key: 'railDepthFt', label: 'Rail Height', kind: 'length', default: 5.5 * IN, min: 1 * IN, max: 1, conditionalOn: on(['rail', 'doubleFace']) },
          { key: 'glassThicknessFt', label: 'Glass Thickness', kind: 'length', default: 0.5 * IN, min: 0.25 * IN, max: 1 * IN, conditionalOn: on(['glass']) },
          { key: 'panelGapFt', label: 'Gap Between Panels', kind: 'length', default: 2 * IN, min: 0, max: 0.5, conditionalOn: on(['glass']) },
          { key: 'wallThicknessFt', label: 'Wall Thickness', kind: 'length', default: 8 * IN, min: 4 * IN, max: 2, conditionalOn: on(['masonry']) },
          // CAP (2026-10-12, Matt): a separate cap, or the stucco simply wraps over the top
          {
            key: 'capStyle', label: 'Cap', kind: 'choice', default: 'cap', conditionalOn: on(['masonry']),
            choices: [
              { value: 'cap', label: 'Separate Cap' },
              { value: 'none', label: 'None (stucco over the top)' },
            ],
          },
          { key: 'capHeightFt', label: 'Cap Height', kind: 'length', default: 2 * IN, min: 0, max: 0.5, conditionalOn: [on(['masonry']), { param: 'capStyle', equals: ['cap'] }] },
          { key: 'capOverhangFt', label: 'Cap Overhang', kind: 'length', default: 1 * IN, min: 0, max: 0.5, conditionalOn: [on(['masonry']), { param: 'capStyle', equals: ['cap'] }] },
          // PIERS (2026-10-12, Matt): off = one continuous wall, no piers — the run is the
          // whole path, corners close solid, the wall's own ends are a gate's jambs
          { key: 'piers', label: 'Piers', kind: 'bool', default: true, conditionalOn: on(['masonry']) },
          { key: 'pierExtraFt', label: 'Pier Above Wall', kind: 'length', default: 4 * IN, min: 0, max: 2, conditionalOn: [on(['masonry']), { param: 'piers', equals: [true] }] },
        ],
      },
      {
        label: 'Materials',
        params: [
          // '' = the style's built-in look
          { key: 'surfaceMaterial', label: 'Posts / Frame', kind: 'material', default: '' },
          { key: 'infillMaterial', label: 'Infill', kind: 'material', default: '' },
        ],
      },
    ],
  },
  instanceParams: {
    groups: [
      {
        // COLORS (2026-10-06): the Paint Brush colors the fence's boards / infill; the frame takes its project standard
        label: 'Color',
        params: [
          { key: 'infillColor', label: 'Infill Color', kind: 'data', default: null, hidden: true },
          { key: 'frameColor', label: 'Frame Color', kind: 'data', default: null, hidden: true },
        ],
      },
      {
        label: 'Constraints',
        params: [
          { key: 'path', label: 'Path', kind: 'path2d', default: null },
          // HEIGHT above the ground (Matt: "adjustable, similar to how walls heights are") —
          // nullable override shadowing the type's `heightFt` (the casework pattern): read
          // `el.params.heightFt ?? el.typeParams.heightFt`
          { key: 'heightFt', label: 'Height', kind: 'length', default: null, optional: true, min: 1, max: 12 },
          { key: 'baseOffsetFt', label: 'Base Offset', kind: 'length', default: 0, min: -20, max: 20 },
          {
            key: 'slopeOverride', label: 'On Slopes', kind: 'choice', default: '',
            choices: [
              { value: '', label: '(Use Type Default)' },
              { value: 'stepped', label: 'Stepped' },
              { value: 'raked', label: 'Raked' },
            ],
          },
          {
            // STAGE 4 posts: null = automatic; else this fence's own post stations (feet
            // along the drawn path from its start, vertices implied) — fencePosts.ts. Set by
            // the post grips / Add post / Reset posts, never a form field
            key: 'posts', label: 'Posts', kind: 'data', default: null, hidden: true,
          },
          {
            // the railing model: PICKED hosts (terrain / floors / stairs / ramps), any
            // number; managed by the pet palette's "Host to…", never a form field
            key: 'hostIds', label: 'Host Targets', kind: 'data',
            default: null, positional: true, hidden: true,
          },
        ],
      },
    ],
  },
}
